import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, MapPin, Sparkles } from 'lucide-react';
import { API } from '@/App';
import { SHARE_DOMAIN } from '@/lib/shareDomain';
import PollenDotsMap from '@/components/PollenDotsMap';
import ShareMapMenu from '@/components/ShareMapMenu';

/**
 * PollenConnectionsMap — every customer's Pollen Dots overlaid on a single map.
 * An anonymized "network of gentle connection" view: no names, just places.
 */
const PollenConnectionsMap = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Role filter — all types on by default. Toggling chips hides/shows markers live.
  const [roleFilter, setRoleFilter] = useState({ place: true, hotel: true, guide: true, driver: true });
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = localStorage.getItem('session_token');
        const res = await fetch(`${API}/pollen/dashboard/connections-map`, {
          credentials: 'include',
          headers: t ? { Authorization: `Bearer ${t}` } : {},
        });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setData(d);
      } catch (e) { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="connections-map-card">
        <CardContent className="h-[360px] flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const totals = data.totals || {};
  const pinsAll = [
    ...(data.places  || []).map(p => ({ ...p, type: 'place'  })),
    ...(data.hotels  || []).map(p => ({ ...p, type: 'hotel'  })),
    ...(data.guides  || []).map(p => ({ ...p, type: 'guide'  })),
    ...(data.drivers || []).map(p => ({ ...p, type: 'driver' })),
  ];
  const pins = pinsAll.filter(p => roleFilter[p.type]);
  const hasAny = pinsAll.length > 0;
  const shareUrl = `${SHARE_DOMAIN}/api/og/network`;

  const toggle = (role) => setRoleFilter(rf => ({ ...rf, [role]: !rf[role] }));

  return (
    <Card data-testid="connections-map-card" className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Our Network of Connection
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1 max-w-lg">
              Every place touched, every hotel stayed in, every guide and driver connected — across every journey Pollen Dots has carried.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Toggleable role chips — click each to hide/show its dots on the map. */}
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <RoleChip role="place"  tint="teal"  label="places"  value={totals.places}  active={roleFilter.place}  onClick={() => toggle('place')} />
              <RoleChip role="hotel"  tint="blue"  label="hotels"  value={totals.hotels}  active={roleFilter.hotel}  onClick={() => toggle('hotel')} />
              <RoleChip role="guide"  tint="rose"  label="guides"  value={totals.guides}  active={roleFilter.guide}  onClick={() => toggle('guide')} />
              <RoleChip role="driver" tint="gold"  label="drivers" value={totals.drivers} active={roleFilter.driver} onClick={() => toggle('driver')} />
            </div>
            {hasAny && (
              <ShareMapMenu
                mapRef={mapRef}
                shareUrl={shareUrl}
                whatsappText="Here's the Pollen Dots network — every place we've gently connected through travel."
                filename="pollen-dots-network"
              />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {!hasAny ? (
          <div className="h-[280px] flex items-center justify-center text-xs text-muted-foreground">
            <MapPin className="w-5 h-5 mr-2 text-muted-foreground/40" />
            Pin your locations' Google Maps URLs in Database → Locations and dots will appear here.
          </div>
        ) : (
          <PollenDotsMap ref={mapRef} pins={pins} height={360} zoom={4} />
        )}
      </CardContent>
    </Card>
  );
};

/** Toggle chip next to the dashboard map — click to hide/show that role's pins live. */
const RoleChip = ({ role, tint, label, value, active, onClick }) => {
  const tints = {
    teal: { bg: 'bg-teal-600', text: 'text-teal-700', border: 'border-teal-300' },
    blue: { bg: 'bg-blue-600', text: 'text-blue-700', border: 'border-blue-300' },
    rose: { bg: 'bg-rose-600', text: 'text-rose-700', border: 'border-rose-300' },
    gold: { bg: 'bg-yellow-500', text: 'text-yellow-700', border: 'border-yellow-300' },
  }[tint] || { bg: 'bg-slate-600', text: 'text-slate-700', border: 'border-slate-300' };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`map-role-${role}`}
      data-active={active ? 'true' : 'false'}
      aria-pressed={active}
      title={active ? `Click to hide ${label}` : `Click to show ${label}`}
      className={`flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] transition
        ${active
          ? `${tints.border} bg-white shadow-sm ${tints.text}`
          : 'border-transparent bg-muted/60 text-muted-foreground/60 opacity-70 hover:opacity-100'}`}
    >
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${active ? tints.bg : 'bg-muted-foreground/40'}`} />
      <span className="font-semibold tabular-nums">{value ?? 0}</span>
      <span>{label}</span>
    </button>
  );
};

export default PollenConnectionsMap;
